import './dashboard.css'
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import Header from '../components/header'
import CustomToaster from '../components/custom-toaster'
import { authFetch, getDomain } from '../utils/apis/api'

const BASE_URL = getDomain()
const PAGE_SIZE = 20

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${sessionStorage.getItem('token')}`,
})

const authOnlyHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem('token')}`,
})

const emptyStaffForm = {
  name: '',
  designation: '',
  description: '',
}

const dashboardSections = [
  { id: 'communications', label: 'Communications' },
  { id: 'operations', label: 'Operations' },
  { id: 'people', label: 'People' },
]

const formatDate = (iso) => {
  const timezone = sessionStorage.getItem('timezone') || undefined

  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  })
}

const totalPages = (total) => Math.max(1, Math.ceil(total / PAGE_SIZE))

const Dashboard = () => {
  const navigate = useNavigate()
  const [activeSection, setActiveSection] = useState('communications')

  // notifications
  const [notifications, setNotifications] = useState([])
  const [notifPage, setNotifPage] = useState(1)
  const [notifTotal, setNotifTotal] = useState(0)

  // work orders
  const [workOrders, setWorkOrders] = useState([])
  const [workPage, setWorkPage] = useState(1)
  const [workTotal, setWorkTotal] = useState(0)

  // reviews
  const [reviews, setReviews] = useState([])
  const [reviewPage, setReviewPage] = useState(1)
  const [reviewTotal, setReviewTotal] = useState(0)

  // staff
  const [staff, setStaff] = useState([])
  const [staffForm, setStaffForm] = useState(emptyStaffForm)
  const [editingStaffId, setEditingStaffId] = useState(null)
  const [isSavingStaff, setIsSavingStaff] = useState(false)

  // pending signups
  const [pendingSignups, setPendingSignups] = useState([])
  const [approvingSignupId, setApprovingSignupId] = useState(null)

  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastVariant, setToastVariant] = useState('success')

  const showAlert = (msg, variant = 'success') => {
    setToastMessage(msg)
    setToastVariant(variant)
    setShowToast(true)
  }

  // ───────── Fetch Notifications ─────────
  const fetchNotifications = useCallback(
    async (signal) => {
      try {
        const res = await authFetch(
          `${BASE_URL}admin/notifications?page=${notifPage}&page_size=${PAGE_SIZE}`,
          { headers: authHeaders(), signal },
        )

        if (!res.ok) throw new Error()

        const data = await res.json()

        setNotifications(data.items)
        setNotifTotal(data.total)
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err)
      }
    },
    [notifPage],
  )

  // ───────── Fetch Work Orders ─────────
  const fetchWorkOrders = useCallback(
    async (signal) => {
      try {
        const res = await authFetch(
          `${BASE_URL}admin/work-orders?page=${workPage}&page_size=${PAGE_SIZE}`,
          { headers: authHeaders(), signal },
        )

        if (!res.ok) throw new Error()

        const data = await res.json()

        setWorkOrders(data.items)
        setWorkTotal(data.total)
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err)
      }
    },
    [workPage],
  )

  // ───────── Fetch Reviews ─────────
  const fetchReviews = useCallback(
    async (signal) => {
      try {
        const res = await authFetch(
          `${BASE_URL}admin/reviews?page=${reviewPage}&page_size=${PAGE_SIZE}`,
          { headers: authHeaders(), signal },
        )

        if (!res.ok) throw new Error()

        const data = await res.json()

        setReviews(data.items)
        setReviewTotal(data.total)
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err)
      }
    },
    [reviewPage],
  )

  // ───────── Fetch Staff ─────────
  const fetchStaff = useCallback(async (signal) => {
    try {
      const res = await authFetch(`${BASE_URL}staff`, {
        headers: authHeaders(),
        signal,
      })

      if (!res.ok) throw new Error()

      const data = await res.json()

      setStaff(data)
    } catch (err) {
      if (err.name !== 'AbortError') console.error(err)
    }
  }, [])

  // ───────── Fetch Pending Signups ─────────
  const fetchPendingSignups = useCallback(async (signal) => {
    try {
      const res = await authFetch(`${BASE_URL}admin/pending-signups`, {
        headers: authHeaders(),
        signal,
      })

      if (!res.ok) throw new Error()

      const data = await res.json()

      setPendingSignups(data)
    } catch (err) {
      if (err.name !== 'AbortError') console.error(err)
    }
  }, [])

  // Auth check + one-time fetches (staff & pending signups never need to
  // re-run just because a pagination counter changed).
  // AbortController ensures the first in-flight request is cancelled when
  // React StrictMode unmounts/remounts the component in development.
  useEffect(() => {
    const token = sessionStorage.getItem('token')
    const userType = sessionStorage.getItem('userType')

    if (!token || userType !== 'admin') {
      navigate('/')
      return
    }

    const controller = new AbortController()
    fetchStaff(controller.signal)
    fetchPendingSignups(controller.signal)
    return () => controller.abort()
  }, [navigate, fetchStaff, fetchPendingSignups])

  // Re-fetch notifications only when notifPage changes.
  // fetchNotifications is memoised with [notifPage], so its reference
  // updates whenever notifPage changes, triggering this effect.
  useEffect(() => {
    const controller = new AbortController()
    fetchNotifications(controller.signal)
    return () => controller.abort()
  }, [fetchNotifications])

  // Re-fetch work orders only when workPage changes
  useEffect(() => {
    const controller = new AbortController()
    fetchWorkOrders(controller.signal)
    return () => controller.abort()
  }, [fetchWorkOrders])

  // Re-fetch reviews only when reviewPage changes
  useEffect(() => {
    const controller = new AbortController()
    fetchReviews(controller.signal)
    return () => controller.abort()
  }, [fetchReviews])

  // ───────── Create Notification ─────────
  const handleCreate = async (e) => {
    e.preventDefault()

    if (!title.trim() || !message.trim()) return

    setIsSubmitting(true)

    try {
      const res = await authFetch(`${BASE_URL}admin/notifications`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          title: title.trim(),
          message: message.trim(),
        }),
      })

      if (!res.ok) throw new Error()

      setTitle('')
      setMessage('')

      showAlert('Notification sent!', 'success')

      setNotifPage(1)
      fetchNotifications()
    } catch {
      showAlert('Failed to send notification.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  // ───────── Delete Notification ─────────
  const handleDelete = async (id) => {
    try {
      const res = await authFetch(`${BASE_URL}admin/notifications/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      })

      if (!res.ok) throw new Error()

      showAlert('Notification removed.', 'success')

      fetchNotifications()
    } catch {
      showAlert('Failed to remove notification.', 'error')
    }
  }

  // ───────── Staff Create / Update ─────────
  const handleStaffSubmit = async (e) => {
    e.preventDefault()

    if (
      !staffForm.name.trim() ||
      !staffForm.designation.trim() ||
      !staffForm.description.trim()
    ) {
      return
    }

    const formData = new FormData()
    formData.append('name', staffForm.name.trim())
    formData.append('designation', staffForm.designation.trim())
    formData.append('description', staffForm.description.trim())

    setIsSavingStaff(true)

    try {
      const isEditing = Boolean(editingStaffId)
      const res = await authFetch(
        isEditing
          ? `${BASE_URL}admin/staff/${editingStaffId}`
          : `${BASE_URL}admin/staff`,
        {
          method: isEditing ? 'PATCH' : 'POST',
          headers: authOnlyHeaders(),
          body: formData,
        },
      )

      if (!res.ok) throw new Error()

      setStaffForm(emptyStaffForm)
      setEditingStaffId(null)
      showAlert(
        isEditing ? 'Staff member updated.' : 'Staff member created.',
        'success',
      )
      fetchStaff()
    } catch {
      showAlert('Failed to save staff member.', 'error')
    } finally {
      setIsSavingStaff(false)
    }
  }

  const handleStaffEdit = (staffMember) => {
    setEditingStaffId(staffMember.id)
    setStaffForm({
      name: staffMember.name || '',
      designation: staffMember.designation || '',
      description: staffMember.description || '',
    })
  }

  const handleStaffCancel = () => {
    setEditingStaffId(null)
    setStaffForm(emptyStaffForm)
  }

  // ───────── Approve Signup ─────────
  const handleApproveSignup = async (userId) => {
    setApprovingSignupId(userId)

    try {
      const res = await authFetch(`${BASE_URL}admin/approve-signup/${userId}`, {
        method: 'POST',
        headers: authHeaders(),
      })

      if (!res.ok) throw new Error()

      showAlert('Signup approved.', 'success')
      fetchPendingSignups()
    } catch {
      showAlert('Failed to approve signup.', 'error')
    } finally {
      setApprovingSignupId(null)
    }
  }

  return (
    <>
      <Header onBurgerClick={() => {}} />
      <div className="db-page">
        <div className="db-topbar">
          <div>
            <h2 className="db-page-title">Dashboard</h2>
            <p className="db-page-sub">
              Manage notifications, work orders, reviews, staff, and signups
            </p>
          </div>
        </div>

        <div className="db-stats-row">
          <button
            className="db-stat-card"
            type="button"
            onClick={() => setActiveSection('communications')}
          >
            <span
              className="db-stat-accent"
              style={{ background: '#40bfee' }}
            />
            <span className="db-stat-body">
              <span className="db-stat-label">Notifications</span>
              <span className="db-stat-value">{notifTotal}</span>
              <span className="db-stat-sub">Broadcast history</span>
            </span>
          </button>

          <button
            className="db-stat-card"
            type="button"
            onClick={() => setActiveSection('operations')}
          >
            <span
              className="db-stat-accent"
              style={{ background: '#f59e0b' }}
            />
            <span className="db-stat-body">
              <span className="db-stat-label">Work Orders</span>
              <span className="db-stat-value">{workTotal}</span>
              <span className="db-stat-sub">Maintenance queue</span>
            </span>
          </button>

          <button
            className="db-stat-card"
            type="button"
            onClick={() => setActiveSection('operations')}
          >
            <span
              className="db-stat-accent"
              style={{ background: '#2563eb' }}
            />
            <span className="db-stat-body">
              <span className="db-stat-label">Reviews</span>
              <span className="db-stat-value">{reviewTotal}</span>
              <span className="db-stat-sub">Resident feedback</span>
            </span>
          </button>

          <button
            className="db-stat-card"
            type="button"
            onClick={() => setActiveSection('people')}
          >
            <span
              className="db-stat-accent"
              style={{ background: '#10b981' }}
            />
            <span className="db-stat-body">
              <span className="db-stat-label">Pending Signups</span>
              <span className="db-stat-value">{pendingSignups.length}</span>
              <span className="db-stat-sub">{staff.length} staff profiles</span>
            </span>
          </button>
        </div>

        <div className="db-section-tabs">
          {dashboardSections.map((section) => (
            <button
              key={section.id}
              className={`db-section-tab${
                activeSection === section.id ? ' db-section-tab-active' : ''
              }`}
              type="button"
              onClick={() => setActiveSection(section.id)}
            >
              {section.label}
            </button>
          ))}
        </div>

        <div className="db-grid">
          {activeSection === 'communications' && (
            <>
              {/* ───────── Notifications Panel ───────── */}
              <div className="db-panel">
                <div className="db-panel-header">
                  <h3 className="db-panel-title">
                    Send Notification
                    <span className="db-badge">{notifTotal}</span>
                  </h3>
                  <p className="db-panel-desc">
                    Broadcast a message to all users.
                  </p>
                </div>

                <form onSubmit={handleCreate} className="db-notif-form">
                  <input
                    className="db-input"
                    type="text"
                    placeholder="Title"
                    maxLength={255}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />

                  <textarea
                    className="db-textarea"
                    placeholder="Message"
                    maxLength={5000}
                    rows={5}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                  />

                  <button
                    className="db-btn-primary"
                    type="submit"
                    disabled={isSubmitting || !title.trim() || !message.trim()}
                  >
                    {isSubmitting ? 'Sending…' : 'Send to all users'}
                  </button>
                </form>

                <ul className="db-notif-list">
                  {notifications.map((n) => (
                    <li
                      key={n.id}
                      className={`db-notif-item${
                        n.is_active ? '' : ' db-notif-inactive'
                      }`}
                    >
                      <div className="db-notif-body">
                        <div className="db-notif-title">{n.title}</div>

                        <div className="db-notif-message">{n.message}</div>

                        <div className="db-notif-meta">
                          {formatDate(n.created_at)}
                        </div>
                      </div>

                      {n.is_active && (
                        <button
                          className="db-notif-delete"
                          onClick={() => handleDelete(n.id)}
                        >
                          ✕
                        </button>
                      )}
                    </li>
                  ))}
                </ul>

                {/* pagination */}
                <div className="db-pagination">
                  <button
                    className="pagination-btn"
                    disabled={notifPage === 1}
                    onClick={() => setNotifPage((p) => p - 1)}
                  >
                    Prev
                  </button>

                  <span>
                    Page {notifPage} / {totalPages(notifTotal)}
                  </span>

                  <button
                    className="pagination-btn"
                    disabled={notifPage * PAGE_SIZE >= notifTotal}
                    onClick={() => setNotifPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}

          {activeSection === 'operations' && (
            <>
              {/* ───────── Work Orders Panel ───────── */}
              <div className="db-panel">
                <div className="db-panel-header">
                  <h3 className="db-panel-title">
                    Work Orders
                    <span className="db-badge">{workTotal}</span>
                  </h3>

                  <p className="db-panel-desc">Recent maintenance requests</p>
                </div>

                {workOrders.length === 0 ? (
                  <p className="db-empty">No work orders found.</p>
                ) : (
                  <>
                    <ul className="db-notif-list">
                      {workOrders.map((w) => (
                        <li key={w.id} className="db-notif-item">
                          <div className="db-notif-body">
                            <div className="db-notif-title">
                              {w.name} — {w.property}
                            </div>

                            <div className="db-notif-message">
                              {w.description}
                            </div>

                            <div className="db-notif-meta">
                              {w.priority} • {w.category} •{' '}
                              {formatDate(w.created_at)}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>

                    <div className="db-pagination">
                      <button
                        className="pagination-btn"
                        disabled={workPage === 1}
                        onClick={() => setWorkPage((p) => p - 1)}
                      >
                        Prev
                      </button>

                      <span>
                        Page {workPage} / {totalPages(workTotal)}
                      </span>

                      <button
                        className="pagination-btn"
                        disabled={workPage * PAGE_SIZE >= workTotal}
                        onClick={() => setWorkPage((p) => p + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* ───────── Reviews Panel ───────── */}
              <div className="db-panel">
                <div className="db-panel-header">
                  <h3 className="db-panel-title">
                    Reviews
                    <span className="db-badge">{reviewTotal}</span>
                  </h3>

                  <p className="db-panel-desc">Recent property feedback</p>
                </div>

                {reviews.length === 0 ? (
                  <p className="db-empty">No reviews found.</p>
                ) : (
                  <>
                    <ul className="db-notif-list">
                      {reviews.map((review) => (
                        <li key={review.id} className="db-notif-item">
                          <div className="db-notif-body">
                            <div className="db-notif-title">
                              <span>{review.review_title}</span>
                              <span className="db-review-score">
                                {review.overall_rating}/5
                              </span>
                            </div>

                            <div className="db-notif-message">
                              {review.review_text}
                            </div>

                            <div className="db-notif-meta">
                              User #{review.user_id || 'Unknown'} •{' '}
                              {review.would_recommend
                                ? 'Would recommend'
                                : 'Would not recommend'}{' '}
                              • {formatDate(review.created_at)}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>

                    <div className="db-pagination">
                      <button
                        className="pagination-btn"
                        disabled={reviewPage === 1}
                        onClick={() => setReviewPage((p) => p - 1)}
                      >
                        Prev
                      </button>

                      <span>
                        Page {reviewPage} / {totalPages(reviewTotal)}
                      </span>

                      <button
                        className="pagination-btn"
                        disabled={reviewPage * PAGE_SIZE >= reviewTotal}
                        onClick={() => setReviewPage((p) => p + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {activeSection === 'people' && (
            <>
              {/* ───────── Staff Panel ───────── */}
              <div className="db-panel">
                <div className="db-panel-header">
                  <h3 className="db-panel-title">
                    Staff
                    <span className="db-badge">{staff.length}</span>
                  </h3>

                  <p className="db-panel-desc">
                    Create and update staff profiles
                  </p>
                </div>

                <form onSubmit={handleStaffSubmit} className="db-notif-form">
                  <input
                    className="db-input"
                    type="text"
                    placeholder="Name"
                    value={staffForm.name}
                    onChange={(e) =>
                      setStaffForm((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    required
                  />

                  <input
                    className="db-input"
                    type="text"
                    placeholder="Designation"
                    value={staffForm.designation}
                    onChange={(e) =>
                      setStaffForm((prev) => ({
                        ...prev,
                        designation: e.target.value,
                      }))
                    }
                    required
                  />

                  <textarea
                    className="db-textarea"
                    placeholder="Description"
                    rows={4}
                    value={staffForm.description}
                    onChange={(e) =>
                      setStaffForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    required
                  />

                  <div className="db-form-actions">
                    <button
                      className="db-btn-primary"
                      type="submit"
                      disabled={
                        isSavingStaff ||
                        !staffForm.name.trim() ||
                        !staffForm.designation.trim() ||
                        !staffForm.description.trim()
                      }
                    >
                      {isSavingStaff
                        ? 'Saving...'
                        : editingStaffId
                          ? 'Update staff'
                          : 'Create staff'}
                    </button>

                    {editingStaffId && (
                      <button
                        className="db-btn-secondary"
                        type="button"
                        onClick={handleStaffCancel}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>

                {staff.length === 0 ? (
                  <p className="db-empty">No staff profiles found.</p>
                ) : (
                  <ul className="db-notif-list">
                    {staff.map((staffMember) => (
                      <li key={staffMember.id} className="db-notif-item">
                        <div className="db-notif-body">
                          <div className="db-notif-title">
                            {staffMember.name}
                          </div>

                          <div className="db-notif-message">
                            {staffMember.designation}
                          </div>

                          <div className="db-notif-meta">
                            {staffMember.description}
                          </div>
                        </div>

                        <button
                          className="db-list-action"
                          type="button"
                          onClick={() => handleStaffEdit(staffMember)}
                        >
                          Edit
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* ───────── Pending Signups Panel ───────── */}
              <div className="db-panel">
                <div className="db-panel-header">
                  <h3 className="db-panel-title">
                    Pending Signups
                    <span className="db-badge">{pendingSignups.length}</span>
                  </h3>

                  <p className="db-panel-desc">Review and approve new users</p>
                </div>

                {pendingSignups.length === 0 ? (
                  <p className="db-empty">No pending signups.</p>
                ) : (
                  <ul className="db-notif-list">
                    {pendingSignups.map((user) => (
                      <li key={user.id} className="db-notif-item">
                        <div className="db-notif-body">
                          <div className="db-notif-title">
                            {user.username || user.email}
                          </div>

                          <div className="db-notif-message">
                            {[user.first_name, user.last_name]
                              .filter(Boolean)
                              .join(' ') || user.email}
                          </div>

                          <div className="db-notif-meta">
                            {user.email} • Signed up{' '}
                            {formatDate(user.created_at)}
                          </div>
                        </div>

                        <button
                          className="db-list-action"
                          type="button"
                          disabled={approvingSignupId === user.id}
                          onClick={() => handleApproveSignup(user.id)}
                        >
                          {approvingSignupId === user.id
                            ? 'Approving...'
                            : 'Approve'}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {toastMessage && (
        <CustomToaster
          show={showToast}
          onClose={() => setShowToast(false)}
          message={toastMessage}
          type={toastVariant}
        />
      )}
    </>
  )
}

export default Dashboard
